import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Drawer, IconButton, List, Popover, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarViewWeekOutlinedIcon from '@mui/icons-material/CalendarViewWeekOutlined';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import styled from '@emotion/styled';
import { ColumnMetaDto } from '@api/models/analytics/ColumnMetaDto';
import { GROUP_COLUMN_KEY } from '../analyticsColumns';
import { ColumnPickerRow } from './ColumnPickerRow';
import { QcsChip } from '../../common/basic/QcsChip';

const DESKTOP_WIDTH = 360;
const LIST_MAX_HEIGHT = '60vh';

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 8px 16px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 16px 12px 16px;
`;

const MobileGrabber = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.18);
  margin: 8px auto 0 auto;
`;

interface Props {
  columns: ColumnMetaDto[];
  visibleColumns: string[];
  groupColumnLabelKey?: string;
  onChange: (next: string[]) => void;
}

const resolveEffective = (columns: ColumnMetaDto[], visible: string[]): string[] => {
  const known = new Set(columns.map((c) => c.key));
  const filtered = visible.filter((k) => known.has(k));
  if (filtered.length > 0) return filtered;
  return columns.filter((c) => c.defaultVisible).map((c) => c.key);
};

// Stable display order: keys from `visible` first (user-controlled), then any remaining metadata columns.
// Independent of checked state so toggling never reorders rows.
const buildInitialOrder = (columns: ColumnMetaDto[], visible: string[]): string[] => {
  const known = new Set(columns.map((c) => c.key));
  const seen = new Set<string>();
  const result: string[] = [];
  for (const k of visible) {
    if (known.has(k) && !seen.has(k)) {
      result.push(k);
      seen.add(k);
    }
  }
  for (const col of columns) {
    if (!seen.has(col.key)) result.push(col.key);
  }
  return result;
};

export const ColumnPicker: FC<Props> = ({ columns, visibleColumns, groupColumnLabelKey, onChange }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = anchorEl !== null;
  const close = () => setAnchorEl(null);

  // Persisted user-defined ordering (mutated on drag). Metadata sync is derived in `order` below.
  const [userOrder, setUserOrder] = useState<string[]>(() => buildInitialOrder(columns, visibleColumns));

  // Effective display order: user-defined first (filtered to known keys), then any new metadata keys.
  const order = useMemo(() => {
    const known = new Set(columns.map((c) => c.key));
    const kept = userOrder.filter((k) => known.has(k));
    const seen = new Set(kept);
    const missing = columns.filter((c) => !seen.has(c.key)).map((c) => c.key);
    return [...kept, ...missing];
  }, [columns, userOrder]);

  const checkedSet = useMemo(() => new Set(resolveEffective(columns, visibleColumns)), [columns, visibleColumns]);

  const items = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    const out: { key: string; col: ColumnMetaDto; checked: boolean }[] = [];
    for (const k of order) {
      const col = byKey.get(k);
      if (col) out.push({ key: k, col, checked: checkedSet.has(k) });
    }
    return out;
  }, [columns, order, checkedSet]);

  const itemIds = useMemo(() => items.map((i) => i.key), [items]);
  const isDefaults = visibleColumns.length === 0;
  const selectedCount = checkedSet.size;
  const totalCount = columns.length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleToggle = (key: string) => {
    const next = new Set(checkedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(order.filter((k) => next.has(k)));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(String(active.id));
    const newIndex = order.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(order, oldIndex, newIndex);
    setUserOrder(next);
    onChange(next.filter((k) => checkedSet.has(k)));
  };

  const handleReset = () => onChange([]);

  const body = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: isMobile ? '100%' : DESKTOP_WIDTH }}>
      <Header>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">{t('analytics.columns.pickerTitle')}</Typography>
          <QcsChip label={t('analytics.columns.pickerCount', { selected: selectedCount, total: totalCount })} />
        </Box>
        {isMobile && (
          <IconButton size="small" onClick={close} aria-label={t('common.close')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Header>
      <Divider />
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <List dense disablePadding sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}>
            {items.map((i) => (
              <ColumnPickerRow
                key={i.key}
                id={i.key}
                label={i.col.key === GROUP_COLUMN_KEY ? t(groupColumnLabelKey ?? 'analytics.table.groupColumn') : t(i.col.labelKey)}
                checked={i.checked}
                onToggle={() => handleToggle(i.key)}
              />
            ))}
          </List>
        </SortableContext>
      </DndContext>
      <Divider />
      <Footer>
        <Button size="small" onClick={handleReset} disabled={isDefaults}>
          {t('analytics.columns.resetDefaults')}
        </Button>
        <Button size="small" variant="contained" onClick={close}>
          {t('common.done')}
        </Button>
      </Footer>
    </Box>
  );

  return (
    <>
      <Tooltip title={t('analytics.columns.tooltip')}>
        <span>
          <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label={t('analytics.columns.tooltip')}>
            <CalendarViewWeekOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {isMobile ? (
        <Drawer anchor="bottom" open={open} onClose={close} slotProps={{ paper: { sx: { borderTopLeftRadius: 12, borderTopRightRadius: 12 } } }}>
          <MobileGrabber />
          {body}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {body}
        </Popover>
      )}
    </>
  );
};
